import { RARITY_MEANINGS, type OfficialRarity } from "@/lib/tcg-cards";

const EXTRA: Record<string, string> = {
  PSD: "Pal Stars Paldeck entry — a database-only stat sheet, not a printed Palworld OFFICIAL CARD GAME card.",
};

const TONE: Record<string, string> = {
  C: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  U: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  R: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  RR: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
  SR: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  SP: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
  SSP: "bg-rose-500/15 text-rose-300 border-rose-500/40",
  OSR: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  SSS: "bg-yellow-500/20 text-yellow-300 border-yellow-500/50",
  TD: "bg-teal-500/15 text-teal-300 border-teal-500/40",
  TSR: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  TSP: "bg-pink-500/15 text-pink-300 border-pink-500/40",
  PR: "bg-orange-500/15 text-orange-300 border-orange-500/40",
  PSD: "bg-muted text-muted-foreground border-border",
};

export function rarityMeaning(code: string): string {
  return RARITY_MEANINGS[code as OfficialRarity] ?? EXTRA[code] ?? "Rarity code printed on the card.";
}

/**
 * Rarity code chip. Hovering (or focusing, for keyboard/screen-reader users)
 * reveals the full meaning of the printed rarity code.
 */
export function RarityBadge({ code, className = "" }: { code: string; className?: string }) {
  const meaning = rarityMeaning(code);
  return (
    <span className={`group/rar relative inline-flex ${className}`}>
      <span
        tabIndex={0}
        role="note"
        aria-label={`Rarity ${code}: ${meaning}`}
        title={`${code} — ${meaning}`}
        className={`cursor-help rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wide outline-none focus-visible:ring-2 focus-visible:ring-primary ${TONE[code] ?? TONE.PSD}`}
      >
        {code}
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-[min(16rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg border bg-popover p-2.5 text-[11px] leading-relaxed text-popover-foreground opacity-0 shadow-xl transition-opacity group-hover/rar:opacity-100 group-focus-within/rar:opacity-100"
      >
        <b>{code}</b> — {meaning}
      </span>
    </span>
  );
}

/** Legend strip explaining every printed rarity code on the page. */
export function RarityLegend({ codes }: { codes: string[] }) {
  return (
    <div className="mt-4 rounded-xl border bg-card/40 p-4">
      <h2 className="text-sm font-semibold">Palworld TCG rarity codes explained</h2>
      <p className="aeo-answer mt-1 text-xs text-muted-foreground">
        Palworld OFFICIAL CARD GAME rarity codes run C (Common), U (Uncommon), R (Rare), RR (Double Rare),
        SR (Super Rare), SP (Special), OSR (Over Super Rare), SSP (Super Special Parallel) and SSS
        (Ultra Secret Gold Card). Trial Deck printings use TD, TSR and TSP; promos use PR. Hover any code below
        for its full meaning.
      </p>
      <ul className="mt-3 flex flex-wrap gap-2">
        {codes.map((c) => (
          <li key={c} className="flex items-center gap-1.5">
            <RarityBadge code={c} />
            <span className="text-[11px] text-muted-foreground">{rarityMeaning(c).split(" — ")[0]}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
